// src/contexts/ThemeContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { apiClient } from "../services/api/base";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// Available themes with metadata
const AVAILABLE_THEMES = [
  {
    id: "purple-light",
    name: "Purple Light",
    description: "Default purple theme with light background",
    primary: "#7F5CFF",
    secondary: "#1F3C88",
    isDark: false,
    category: "purple",
  },
  {
    id: "purple-dark",
    name: "Purple Dark",
    description: "Purple theme with dark background",
    primary: "#7F5CFF",
    secondary: "#1F3C88",
    isDark: true,
    category: "purple",
  },
  {
    id: "blue-light",
    name: "Blue Light",
    description: "Professional blue theme with light background",
    primary: "#3b82f6",
    secondary: "#1e40af",
    isDark: false,
    category: "blue",
  },
  {
    id: "blue-dark",
    name: "Blue Dark",
    description: "Professional blue theme with dark background",
    primary: "#60a5fa",
    secondary: "#3730a3",
    isDark: true,
    category: "blue",
  },
  {
    id: "green-light",
    name: "Green Light",
    description: "Fresh green theme with light background",
    primary: "#10b981",
    secondary: "#059669",
    isDark: false,
    category: "green",
  },
  {
    id: "green-dark",
    name: "Green Dark",
    description: "Fresh green theme with dark background",
    primary: "#34d399",
    secondary: "#047857",
    isDark: true,
    category: "green",
  },
  {
    id: "red-light",
    name: "Red Light",
    description: "Bold red theme with light background",
    primary: "#ef4444",
    secondary: "#dc2626",
    isDark: false,
    category: "red",
  },
  {
    id: "red-dark",
    name: "Red Dark",
    description: "Bold red theme with dark background",
    primary: "#f87171",
    secondary: "#b91c1c",
    isDark: true,
    category: "red",
  },
  {
    id: "orange-light",
    name: "Orange Light",
    description: "Vibrant orange theme with light background",
    primary: "#f97316",
    secondary: "#ea580c",
    isDark: false,
    category: "orange",
  },
  {
    id: "orange-dark",
    name: "Orange Dark",
    description: "Vibrant orange theme with dark background",
    primary: "#fb923c",
    secondary: "#c2410c",
    isDark: true,
    category: "orange",
  },
  {
    id: "yellow-light",
    name: "Yellow Light",
    description: "Bright yellow theme with light background",
    primary: "#eab308",
    secondary: "#ca8a04",
    isDark: false,
    category: "yellow",
  },
  {
    id: "yellow-dark",
    name: "Yellow Dark",
    description: "Bright yellow theme with dark background",
    primary: "#facc15",
    secondary: "#a16207",
    isDark: true,
    category: "yellow",
  },
];

export const ThemeProvider = ({ children, user }) => {
  const [currentTheme, setCurrentTheme] = useState("purple-light");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Get theme metadata by ID
   */
  const getThemeById = useCallback((themeId) => {
    return (
      AVAILABLE_THEMES.find((theme) => theme.id === themeId) ||
      AVAILABLE_THEMES[0]
    );
  }, []);

  /**
   * Apply theme class to document element
   */
  const applyThemeToDocument = useCallback(
    (themeId) => {
      const root = document.documentElement;

      // Add transitioning class to disable transitions temporarily
      setIsTransitioning(true);
      root.classList.add("theme-transitioning");

      // Remove all theme classes
      AVAILABLE_THEMES.forEach((theme) => {
        root.classList.remove(`theme-${theme.id}`);
      });

      // Add new theme class
      root.classList.add(`theme-${themeId}`);

      // Store in localStorage
      localStorage.setItem("theme", themeId);

      // Remove transitioning class after a short delay
      setTimeout(() => {
        root.classList.remove("theme-transitioning");
        setIsTransitioning(false);
      }, 50);

      // Update meta theme-color for mobile browsers
      const themeMetaTag = document.querySelector('meta[name="theme-color"]');
      const themeData = getThemeById(themeId);
      if (themeMetaTag && themeData) {
        themeMetaTag.setAttribute("content", themeData.primary);
      }

      // Dispatch custom event for components that need to react to theme changes
      window.dispatchEvent(
        new CustomEvent("themeChanged", {
          detail: {
            theme: themeId,
            themeData,
            isDark: themeData.isDark,
          },
        })
      );
    },
    [getThemeById]
  );

  /**
   * Initialize theme from user data, localStorage, or default
   */
  useEffect(() => {
    const initializeTheme = () => {
      let initialTheme = "purple-light"; // Default fallback

      // Priority 1: User's business theme (from auth)
      if (user?.business?.theme) {
        initialTheme = user.business.theme;
      }
      // Priority 2: localStorage
      else {
        const storedTheme = localStorage.getItem("theme");
        if (storedTheme && AVAILABLE_THEMES.find((t) => t.id === storedTheme)) {
          initialTheme = storedTheme;
        }
      }

      // Priority 3: System preference (for dark mode)
      if (!user?.business?.theme && !localStorage.getItem("theme")) {
        const prefersDark = window.matchMedia(
          "(prefers-color-scheme: dark)"
        ).matches;
        if (prefersDark) {
          initialTheme = "purple-dark";
        }
      }

      setCurrentTheme(initialTheme);
      applyThemeToDocument(initialTheme);
      setIsLoading(false);
    };

    initializeTheme();
  }, [user?.business?.theme, applyThemeToDocument]);

  /**
   * Update theme when user data changes (e.g., after login or business update)
   */
  useEffect(() => {
    if (user?.business?.theme && user.business.theme !== currentTheme) {
      setCurrentTheme(user.business.theme);
      applyThemeToDocument(user.business.theme);
    }
  }, [user?.business?.theme, currentTheme, applyThemeToDocument]);

  /**
   * Preview theme without persisting (for live preview)
   * Only applies to DOM, doesn't update state or persist
   */
  const previewTheme = useCallback(
    (themeId) => {
      // Validate theme
      if (!AVAILABLE_THEMES.find((t) => t.id === themeId)) {
        console.error(`Invalid theme: ${themeId}`);
        return false;
      }

      // Only apply to document, don't update state
      applyThemeToDocument(themeId);
      return true;
    },
    [applyThemeToDocument]
  );

  /**
   * Set theme and persist to backend and localStorage
   * This is the permanent change
   */
  const setTheme = useCallback(
    async (themeId, persist = true) => {
      // Validate theme
      if (!AVAILABLE_THEMES.find((t) => t.id === themeId)) {
        console.error(`Invalid theme: ${themeId}`);
        return false;
      }

      try {
        // Update local state and apply to document immediately
        setCurrentTheme(themeId);
        applyThemeToDocument(themeId);

        // Persist to backend and localStorage if requested
        if (persist) {
          // Store in localStorage
          localStorage.setItem("theme", themeId);

          // Persist to backend if user is authenticated
          if (user?.businessId) {
            try {
              await apiClient.put(`/themes/business/${user.businessId}`, {
                theme: themeId,
              });
            } catch (apiError) {
              console.error("Failed to update theme on server:", apiError);
              // Don't revert local change - user can still use the theme
              // Backend will sync on next page load
            }
          }
        }

        return true;
      } catch (error) {
        console.error("Theme change error:", error);
        return false;
      }
    },
    [user?.businessId, applyThemeToDocument]
  );

  /**
   * Get current theme metadata
   */
  const getTheme = useCallback(() => {
    return getThemeById(currentTheme);
  }, [currentTheme, getThemeById]);

  /**
   * Check if current theme is dark
   */
  const isDarkTheme = useCallback(() => {
    const theme = getTheme();
    return theme?.isDark || false;
  }, [getTheme]);

  /**
   * Toggle between light and dark variants of current color
   */
  const toggleDarkMode = useCallback(() => {
    const currentThemeData = getTheme();
    const targetMode = currentThemeData.isDark ? "light" : "dark";
    const targetTheme = `${currentThemeData.category}-${targetMode}`;

    setTheme(targetTheme);
  }, [getTheme, setTheme]);

  /**
   * Get themes by category
   */
  const getThemesByCategory = useCallback((category) => {
    return AVAILABLE_THEMES.filter((theme) => theme.category === category);
  }, []);

  /**
   * Get themes by mode (light/dark)
   */
  const getThemesByMode = useCallback((isDark) => {
    return AVAILABLE_THEMES.filter((theme) => theme.isDark === isDark);
  }, []);

  /**
   * Get all theme categories
   */
  const getCategories = useCallback(() => {
    return [...new Set(AVAILABLE_THEMES.map((theme) => theme.category))];
  }, []);

  const value = {
    // State
    currentTheme,
    isTransitioning,
    isLoading,

    // Theme data
    availableThemes: AVAILABLE_THEMES,

    // Functions
    setTheme,
    previewTheme,
    getTheme,
    getThemeById,
    isDarkTheme,
    toggleDarkMode,
    getThemesByCategory,
    getThemesByMode,
    getCategories,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
